import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { Pencil, Trash2 } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type ProfileRow = Database['public']['Tables']['profiles']['Row'] & {
  last_seen_at?: string | null;
};

interface Credentials {
  username: string;
  pin: string;
}

interface EditState {
  fullName: string;
  cashierNo: string;
  pin: string; // leave empty to keep current PIN
}

export function CashiersManager() {
  const { t } = useTranslation();
  const [cashiers, setCashiers] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create form
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [cashierNo, setCashierNo] = useState('');
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState<Credentials | null>(null);

  // inline edit
  const [editId, setEditId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ fullName: '', cashierNo: '', pin: '' });
  const [editSaving, setEditSaving] = useState(false);

  // reset PIN
  const [resetPinId, setResetPinId] = useState<string | null>(null);
  const [resetPinBusy, setResetPinBusy] = useState(false);

  async function load() {
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'cashier')
      .order('created_at', { ascending: false });
    if (error) setError(error.message);
    else setCashiers(data);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createCashier() {
    if (!supabase) return;
    if (!username.trim()) {
      setError(t('cashiers.usernameRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    setCreds(null);
    const { data, error } = await supabase.functions.invoke('create-cashier', {
      body: {
        username: username.trim(),
        fullName: fullName.trim(),
        cashierNo: cashierNo ? Number(cashierNo) : undefined,
      },
    });
    setBusy(false);
    if (error) {
      let msg = (error as Error).message;
      try {
        const body = (await (error as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic message */
      }
      setError(msg);
      return;
    }
    const result = data as { cashier: { username: string; pin: string } };
    setCreds(result.cashier);
    setUsername('');
    setFullName('');
    setCashierNo('');
    void load();
  }

  function startEdit(c: ProfileRow) {
    setEditId(c.id);
    setEditState({ fullName: c.full_name ?? '', cashierNo: String(c.cashier_no ?? ''), pin: '' });
  }

  function cancelEdit() {
    setEditId(null);
  }

  async function resetPin(id: string, username: string) {
    if (!supabase) return;
    // Generate a random 4-digit PIN.
    const pin = String(Math.floor(1000 + Math.random() * 9000));
    setResetPinId(id);
    setResetPinBusy(true);
    setError(null);
    const { error: err } = await supabase.functions.invoke('update-cashier', {
      body: { cashierId: id, pin },
    });
    setResetPinBusy(false);
    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic */
      }
      setError(msg);
      setResetPinId(null);
      return;
    }
    // Show the new PIN in the credentials box.
    setCreds({ username, pin });
    setResetPinId(null);
  }

  async function saveEdit(id: string) {
    if (!supabase) return;
    // Validate PIN if provided
    if (editState.pin && !/^\d{4}$/.test(editState.pin)) {
      setError(t('cashiers.pinInvalid'));
      return;
    }
    setEditSaving(true);
    setError(null);
    const { error: err } = await supabase.functions.invoke('update-cashier', {
      body: {
        cashierId: id,
        fullName: editState.fullName,
        cashierNo: editState.cashierNo ? Number(editState.cashierNo) : null,
        ...(editState.pin ? { pin: editState.pin } : {}),
      },
    });
    setEditSaving(false);
    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic */
      }
      setError(msg);
      return;
    }
    setEditId(null);
    void load();
  }

  async function deleteCashier(id: string) {
    if (!supabase) return;
    if (!confirm(t('cashiers.deleteConfirm'))) return;
    setError(null);
    const { error: err } = await supabase.functions.invoke('delete-cashier', {
      body: { cashierId: id },
    });
    if (err) {
      let msg = (err as Error).message;
      try {
        const body = (await (err as unknown as { context: Response }).context.json()) as {
          error?: string;
        };
        if (body?.error) msg = body.error;
      } catch {
        /* use generic */
      }
      setError(msg);
    } else {
      void load();
    }
  }

  return (
    <section className="ui-stack">
      <PageHeader title={t('cashiers.title')} subtitle={t('cashiers.subtitle')} />

      {error && <Callout tone="danger">{error}</Callout>}

      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">{t('cashiers.addPanel')}</span>
        </div>
        <div className="add-form">
          <input
            placeholder={t('cashiers.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void createCashier()}
          />
          <input
            placeholder={t('cashiers.fullNamePlaceholder')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <input
            placeholder={t('cashiers.hdmPlaceholder')}
            type="number"
            min="1"
            value={cashierNo}
            onChange={(e) => setCashierNo(e.target.value)}
            style={{ maxWidth: 110 }}
          />
          <button className="btn" onClick={createCashier} disabled={busy}>
            {busy ? '…' : t('cashiers.create')}
          </button>
        </div>
      </div>

      {creds && (
        <div className="creds-box">
          <strong style={{ fontSize: 13 }}>{t('cashiers.credsTitle')}</strong>
          <p style={{ margin: 0 }}>{t('cashiers.credsNote')}</p>
          <div className="creds-row">
            <span>{t('cashiers.username')}</span>
            <code>{creds.username}</code>
          </div>
          <div className="creds-row">
            <span>{t('cashiers.pin')}</span>
            <code style={{ fontSize: 18, letterSpacing: 4 }}>{creds.pin}</code>
          </div>
          <button
            className="btn-ghost btn-sm"
            onClick={() =>
              void navigator.clipboard.writeText(
                `${t('cashiers.username')}: ${creds.username}\n${t('cashiers.pin')}: ${creds.pin}`,
              )
            }
          >
            {t('common.copy')}
          </button>
        </div>
      )}

      <div className="panel">
        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('cashiers.colUsername')}</th>
                <th>{t('cashiers.colName')}</th>
                <th>{t('cashiers.hdmCol')}</th>
                <th>{t('cashiers.colLastSeen')}</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {cashiers.map((c) =>
                editId === c.id ? (
                  <tr key={c.id}>
                    <td>
                      <code>{c.username ?? t('common.emptyDash')}</code>
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        value={editState.fullName}
                        onChange={(e) => setEditState({ ...editState, fullName: e.target.value })}
                        autoFocus
                        placeholder={t('cashiers.fullNamePlaceholder')}
                      />
                    </td>
                    <td>
                      <input
                        className="table-edit-input"
                        type="number"
                        min="1"
                        value={editState.cashierNo}
                        onChange={(e) => setEditState({ ...editState, cashierNo: e.target.value })}
                        style={{ width: 70 }}
                        placeholder={t('cashiers.hdmShort')}
                      />
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      <input
                        className="table-edit-input"
                        inputMode="numeric"
                        maxLength={4}
                        pattern="\d{4}"
                        value={editState.pin}
                        onChange={(e) =>
                          setEditState({
                            ...editState,
                            pin: e.target.value.replace(/\D/g, '').slice(0, 4),
                          })
                        }
                        style={{ width: 80, letterSpacing: 4 }}
                        placeholder={t('cashiers.newPinPlaceholder')}
                      />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn btn-sm"
                          onClick={() => void saveEdit(c.id)}
                          disabled={editSaving}
                        >
                          {editSaving ? '…' : t('common.save')}
                        </button>
                        <button className="btn-ghost btn-sm" onClick={cancelEdit}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id}>
                    <td>
                      <code>{c.username ?? t('common.emptyDash')}</code>
                    </td>
                    <td>{c.full_name ?? <span className="muted">{t('common.emptyDash')}</span>}</td>
                    <td>{c.cashier_no ?? <span className="muted">{t('common.emptyDash')}</span>}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {c.last_seen_at ? (
                        new Date(c.last_seen_at).toLocaleString()
                      ) : (
                        <span className="muted">{t('common.never')}</span>
                      )}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-icon" onClick={() => startEdit(c)} title={t('common.edit')}>
                          <Pencil className="ui-i" />
                        </button>
                        <button
                          className="btn-ghost btn-sm"
                          onClick={() => void resetPin(c.id, c.username ?? '')}
                          disabled={resetPinBusy && resetPinId === c.id}
                          title={t('cashiers.resetPin')}
                        >
                          {resetPinBusy && resetPinId === c.id ? '…' : t('cashiers.resetPin')}
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => void deleteCashier(c.id)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="ui-i" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
              {cashiers.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: 'center',
                      padding: '32px 20px',
                      color: 'var(--text-subtle)',
                    }}
                  >
                    {t('cashiers.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
